import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Separator,
  Button,
  Badge,
} from '@predoc/cow';
import { format } from 'date-fns';
import {
  type ExistingPatient,
  type FacesheetService,
  type HIEService,
  type ProviderFacilityService,
  type RecordUploadService,
  type RequestDetails,
} from '../../types';
import {
  COMPLETE_MEDICAL_RECORDS,
  SPECIFIC_MEDICAL_RECORDS,
} from '../../constants';
import { getStatusDisplayConfig } from '../../utils/statusDisplayConfig';

interface RequestDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  requestDetails: RequestDetails;
  patient: ExistingPatient;
  isLoading?: boolean;
}

const formatDisplayDateValue = (value?: Date | string) => {
  if (!value) return '—';
  try {
    return format(new Date(value), 'MM/dd/yyyy');
  } catch {
    return '—';
  }
};

const ServiceStatusBadge = ({
  statusGroup,
  statusText,
}: {
  statusGroup: string;
  statusText: string;
}) => {
  const config = getStatusDisplayConfig(statusGroup);
  const IconComponent = config.icon;

  return (
    <Badge
      icon={<IconComponent className={config.iconClassName} />}
      className={config.badgeClassName}
    >
      {statusText || statusGroup || 'Unknown'}
    </Badge>
  );
};

const DetailField = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div>
    <div className="text-sm font-medium text-gray-500">{label}</div>
    <div className="text-sm text-gray-900">{value}</div>
  </div>
);

const ServiceItemContent = ({
  title,
  statusGroup,
  statusText,
  children,
}: {
  title: string;
  statusGroup: string;
  statusText: string;
  children?: React.ReactNode;
}) => (
  <div className="p-4">
    <div className="flex items-start justify-between gap-4">
      <h3 className="flex-1 text-base font-semibold break-words text-gray-900">
        {title}
      </h3>
      <div className="flex min-w-[180px] shrink-0 justify-start">
        <ServiceStatusBadge statusGroup={statusGroup} statusText={statusText} />
      </div>
    </div>
    {children && <div className="mt-4">{children}</div>}
  </div>
);

const HIEServiceSection = ({ service }: { service: HIEService }) => {
  const zipCodes = service.alternativePostalCodes ?? [];

  return (
    <ServiceItemContent
      title="Digital Health Data"
      statusGroup={service.statusGroup}
      statusText={service.statusText}
    >
      {zipCodes.length > 0 && (
        <DetailField label="ZIP Codes" value={zipCodes.join(', ')} />
      )}
    </ServiceItemContent>
  );
};

const MedicationsServiceSection = ({
  service,
}: {
  service: { statusGroup: string; statusText: string };
}) => (
  <ServiceItemContent
    title="Medications Data"
    statusGroup={service.statusGroup}
    statusText={service.statusText}
  />
);

const ProviderFacilityServiceSection = ({
  service,
}: {
  service: ProviderFacilityService;
}) => {
  const formatAddress = () => {
    if (!service.address) return '--';
    const { address1, address2, city, state, postalCode } = service.address;
    const streetAddress = [address1, address2].filter(Boolean).join(', ');
    const stateZip = [state, postalCode].filter(Boolean).join(' ');
    const cityStateZip = [city, stateZip].filter(Boolean).join(', ');
    if (!streetAddress && !cityStateZip) return '--';
    return (
      <>
        {streetAddress && <div>{streetAddress}</div>}
        {cityStateZip && <div>{cityStateZip}</div>}
      </>
    );
  };

  const providerFacilityName = service.providerName
    ? `${service.providerName}${service.facilityName ? ` | ${service.facilityName}` : ''}`
    : service.facilityName;

  const serviceName = providerFacilityName || 'Provider/Facility Service';

  return (
    <ServiceItemContent
      title={serviceName}
      statusGroup={service.statusGroup}
      statusText={service.statusText}
    >
      <div className="grid grid-cols-3 gap-6">
        <DetailField label="Specialty" value={service.specialty || '--'} />
        <DetailField
          label="Records Requested"
          value={
            service.recordsRequested
              ? service.recordsRequested === COMPLETE_MEDICAL_RECORDS
                ? COMPLETE_MEDICAL_RECORDS
                : `${SPECIFIC_MEDICAL_RECORDS} - ${service.recordsRequested}`
              : '--'
          }
        />
        <DetailField
          label="Dates of Service"
          value={`${service.serviceStartDate || '--'} – ${service.serviceEndDate || '--'}`}
        />
      </div>

      <div className="mt-4 grid grid-cols-3 gap-6">
        <DetailField label="Address" value={formatAddress()} />
        <DetailField label="Phone Number" value={service.phoneNumber || '--'} />
      </div>
    </ServiceItemContent>
  );
};

const RecordUploadServiceSection = ({
  service,
}: {
  service: RecordUploadService;
}) => (
  <ServiceItemContent
    title="Medical Record Upload"
    statusGroup={service.statusGroup}
    statusText={service.statusText}
  >
    <DetailField label="File" value={service.fileName} />
  </ServiceItemContent>
);

const FacesheetServiceSection = ({
  service,
}: {
  service: FacesheetService;
}) => (
  <ServiceItemContent
    title={service.name}
    statusGroup={service.statusGroup}
    statusText={service.statusText}
  >
    {service.expedited && <DetailField label="Expedited" value="Yes" />}
  </ServiceItemContent>
);

export const RequestDetailsDialog = ({
  isOpen,
  onClose,
  requestDetails,
  patient,
}: RequestDetailsDialogProps) => {
  const { requestDate } = requestDetails;
  const requestingProviderName = requestDetails.requestFrom || 'Unknown';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="mx-auto !max-w-[946px] p-0"
        aria-describedby={undefined}
      >
        <DialogHeader className="px-6 py-4">
          <DialogTitle className="text-xl font-semibold">
            Request Details
          </DialogTitle>
        </DialogHeader>
        <Separator />

        {/* Scrollable content area */}
        <div className="max-h-[70vh] overflow-y-auto px-6 py-4">
          <div className="rounded-md bg-gray-100 p-4">
            <div className="grid grid-cols-3 gap-6">
              <DetailField
                label="Patient"
                value={`${patient.firstName} ${patient.lastName}`}
              />
              <DetailField
                label="Requesting Provider"
                value={requestingProviderName}
              />
              {requestDetails.releasedTo && (
                <DetailField
                  label="Released To"
                  value={requestDetails.releasedTo}
                />
              )}
            </div>

            <div className="mt-4 grid grid-cols-3 gap-6">
              <DetailField
                label="Request Date"
                value={formatDisplayDateValue(requestDate)}
              />
              {requestDetails.dateNeededBy && (
                <DetailField
                  label="Date Needed By"
                  value={formatDisplayDateValue(requestDetails.dateNeededBy)}
                />
              )}
              <DetailField
                label="Reference ID"
                value={
                  requestDetails.referenceId ? (
                    requestDetails.referenceId
                  ) : (
                    <span className="text-gray-400">- None -</span>
                  )
                }
              />
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-md border border-gray-300">
            <div className="divide-y divide-gray-200">
              {/* HIE Services */}
              {requestDetails.hie?.map((hie, index) => (
                <HIEServiceSection key={`hie-${index}`} service={hie} />
              ))}

              {/* Medications Service */}
              {requestDetails.medications && (
                <MedicationsServiceSection
                  service={requestDetails.medications}
                />
              )}

              {/* Provider/Facility Services */}
              {requestDetails.retrieveFrom?.map((provider, index) => (
                <ProviderFacilityServiceSection
                  key={`provider-${index}`}
                  service={provider}
                />
              ))}

              {/* Record Upload Service */}
              {requestDetails.recordUpload && (
                <RecordUploadServiceSection
                  service={requestDetails.recordUpload}
                />
              )}

              {/* Facesheet Services */}
              {requestDetails.facesheets?.map((facesheet, index) => (
                <FacesheetServiceSection
                  key={`facesheet-${index}`}
                  service={facesheet}
                />
              ))}
            </div>
          </div>
        </div>

        <Separator />
        <DialogFooter className="px-6 py-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
